import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export const useMaintenanceMode = () => {
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    fetchMaintenanceStatus();

    // Subscribe to changes
    const channel = supabase
      .channel('maintenance-settings')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'site_settings',
          filter: 'setting_key=eq.maintenance_mode'
        },
        () => {
          fetchMaintenanceStatus();
        }
      )
      .subscribe();

    return () => {
      // Properly unsubscribe before removing channel
      channel.unsubscribe().then(() => {
        supabase.removeChannel(channel);
      }).catch(() => {
        // Silent fail on cleanup
      });
    };
  }, []);

  const fetchMaintenanceStatus = async () => {
    try {
      // Check if user is authenticated
      const { data: { session } } = await supabase.auth.getSession();
      
      let data: any;
      let error: any;

      if (session?.user) {
        // Authenticated: direct table access
        const result = await supabase
          .from("site_settings")
          .select("setting_value")
          .eq("setting_key", "maintenance_mode")
          .single();
        data = result.data;
        error = result.error;
      } else {
        // Non-authenticated: use public RPC function
        const result = await supabase.rpc('get_public_settings');
        if (result.data) {
          const maintenanceSetting = result.data.find((s: any) => s.setting_key === 'maintenance_mode');
          data = maintenanceSetting ? { setting_value: maintenanceSetting.setting_value } : null;
        }
        error = result.error;
      }

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      const settingValue = data?.setting_value as { enabled?: boolean; message?: string } | null;
      setIsMaintenanceMode(settingValue?.enabled || false);
    } catch (error) {
      console.error("Error fetching maintenance status:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleMaintenanceMode = async (enabled: boolean, message?: string) => {
    try {
      const { error } = await supabase
        .from("site_settings")
        .upsert({
          setting_key: "maintenance_mode",
          setting_value: {
            enabled,
            message: message || "Le site est actuellement en maintenance. Veuillez réessayer plus tard.",
            timestamp: new Date().toISOString()
          }
        }, {
          onConflict: "setting_key"
        });

      if (error) throw error;

      setIsMaintenanceMode(enabled);
      toast({
        title: enabled ? "Mode maintenance activé" : "Mode maintenance désactivé",
        description: enabled 
          ? "Le site affiche maintenant la page de maintenance" 
          : "Le site est de nouveau accessible",
      });

      return true;
    } catch (error) {
      console.error("Error toggling maintenance mode:", error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de modifier le mode maintenance",
      });
      return false;
    }
  };

  return {
    isMaintenanceMode,
    loading,
    toggleMaintenanceMode,
  };
};
